import { useEffect } from "react";

/**
 * Toggle Mapbox Standard "3D objects" (3D buildings, landmarks, trees, etc.)
 * via the basemap import's `show3dObjects` config property.
 *
 * The setting lives on the style import, so it is reset every time the style
 * reloads (e.g. switching between the street and satellite styles). We reapply
 * it on every `style.load` to keep it in sync with the toggle.
 *
 * `setConfigProperty` only works once the style is fully loaded. The style can
 * be transiently "not loaded" for reasons unrelated to this toggle — most
 * notably while the 3D terrain DEM source loads after the terrain toggle is
 * flipped. If we simply skipped the update in that window it would be lost
 * (no `style.load` follows a terrain toggle), which is why toggling terrain and
 * buildings in quick succession could drop one of them. Instead we retry on the
 * next `idle` event so the value is always eventually applied.
 *
 * Only Mapbox Standard styles expose the "basemap" import; on any other style
 * `setConfigProperty` throws, so the call is wrapped to no-op safely.
 *
 * @param {object} mapRef - React ref to the Map component (react-map-gl).
 * @param {boolean} show  - Whether 3D objects should be visible.
 */
export function useShow3dObjects(mapRef, show) {
  useEffect(() => {
    const map = mapRef?.current?.getMap?.();
    if (!map) return;

    // Guards against a superseded toggle (older `show`) applying its value
    // after a newer effect has already run.
    let cancelled = false;

    const apply = () => {
      if (cancelled) return;

      // Style mid-load (style switch or terrain DEM still loading). Retry once
      // the map settles instead of dropping the update.
      if (!map.isStyleLoaded()) {
        map.once("idle", apply);
        return;
      }

      try {
        map.setConfigProperty("basemap", "show3dObjects", show);
      } catch {
        // Non-Standard styles don't expose the "basemap" import; ignore.
      }
    };

    apply();
    // Fires after every setStyle (including street <-> satellite switches).
    map.on("style.load", apply);

    return () => {
      cancelled = true;
      map.off("style.load", apply);
      map.off("idle", apply);
    };
  }, [mapRef, show]);
}
